import { useState, useEffect, useCallback, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { apiPost } from '../../api/axios';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const CATEGORY_COLORS = [
  '#3b82f6', '#ef4444', '#f59e0b', '#10b981',
  '#8b5cf6', '#ec4899', '#06b6d4', '#f97316',
  '#6366f1', '#14b8a6', '#e11d48', '#84cc16',
];

export default function MoneyReports() {
  // Month navigation state
  const [month, setMonth] = useState(new Date());
  // Data states
  const [summary, setSummary] = useState(null);
  const [prevSummary, setPrevSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prevLoading, setPrevLoading] = useState(false);
  const [error, setError] = useState('');
  const [prevError, setPrevError] = useState('');

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    categories: [],
    minAmount: '',
    maxAmount: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  // Chart category filter
  const [chartCategory, setChartCategory] = useState('all');

  // Debounced search term
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch current month summary
  const fetchSummary = useCallback(async (retry = false) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiPost({
        action: 'get_query',
        query: 'money_summary',
        params: {
          year: month.getFullYear(),
          month: month.getMonth() + 1,
        },
      });
      if (res.flag === 'success') {
        setSummary(res.data);
      } else {
        setError(res.msg || 'Failed to load report');
      }
    } catch (err) {
      setError(err.message || 'Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  // Fetch previous month summary for comparison
  const fetchPrevSummary = useCallback(async (retry = false) => {
    setPrevLoading(true);
    setPrevError('');
    try {
      const prevDate = new Date(month);
      prevDate.setMonth(prevDate.getMonth() - 1);
      const res = await apiPost({
        action: 'get_query',
        query: 'money_summary',
        params: {
          year: prevDate.getFullYear(),
          month: prevDate.getMonth() + 1,
        },
      });
      if (res.flag === 'success') {
        setPrevSummary(res.data);
      } else {
        setPrevError(res.msg || 'Failed to load previous month data');
      }
    } catch (err) {
      setPrevError(err.message || 'Network error');
    } finally {
      setPrevLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchSummary();
    fetchPrevSummary();
  }, [fetchSummary, fetchPrevSummary]);

  // Change month handler
  const changeMonth = (delta) => {
    setMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + delta);
      return d;
    });
  };

  // Update filters helper
  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Toggle category selection
  const toggleCategory = (category) => {
    setFilters(prev => {
      const categories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      return { ...prev, categories };
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      type: 'all',
      categories: [],
      minAmount: '',
      maxAmount: '',
      search: '',
      startDate: '',
      endDate: '',
    });
    setChartCategory('all');
  };

  // Derived data: current month transactions filtered
  const filteredTransactions = useMemo(() => {
    if (!summary) return [];
    let txns = summary.transactions || [];

    // Date range filter (within current month)
    if (filters.startDate || filters.endDate) {
      const start = filters.startDate ? new Date(filters.startDate) : null;
      const end = filters.endDate ? new Date(filters.endDate) : null;
      txns = txns.filter(txn => {
        const txnDate = new Date(txn.txn_date);
        if (start && txnDate < start) return false;
        if (end) {
          const endPlusOne = new Date(end);
          endPlusOne.setDate(endPlusOne.getDate() + 1);
          if (txnDate >= endPlusOne) return false;
        }
        return true;
      });
    }

    // Type filter
    if (filters.type !== 'all') {
      txns = txns.filter(txn => txn.type === filters.type);
    }

    // Category filter
    if (filters.categories.length > 0) {
      txns = txns.filter(txn => filters.categories.includes(txn.category_name));
    }

    // Amount range filter
    if (filters.minAmount !== '' || filters.maxAmount !== '') {
      txns = txns.filter(txn => {
        const amount = parseFloat(txn.amount);
        if (filters.minAmount !== '' && amount < parseFloat(filters.minAmount)) return false;
        if (filters.maxAmount !== '' && amount > parseFloat(filters.maxAmount)) return false;
        return true;
      });
    }

    // Search filter on note/category
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      txns = txns.filter(txn =>
        (txn.note && txn.note.toLowerCase().includes(searchLower)) ||
        txn.category_name.toLowerCase().includes(searchLower)
      );
    }

    return txns;
  }, [summary, filters, debouncedSearch]);

  // Previous month transactions filtered similarly (for comparison chart)
  const prevFilteredTransactions = useMemo(() => {
    if (!prevSummary) return [];
    let txns = prevSummary.transactions || [];

    if (filters.type !== 'all') {
      txns = txns.filter(txn => txn.type === filters.type);
    }
    if (filters.categories.length > 0) {
      txns = txns.filter(txn => filters.categories.includes(txn.category_name));
    }
    if (filters.minAmount !== '' || filters.maxAmount !== '') {
      txns = txns.filter(txn => {
        const amount = parseFloat(txn.amount);
        if (filters.minAmount !== '' && amount < parseFloat(filters.minAmount)) return false;
        if (filters.maxAmount !== '' && amount > parseFloat(filters.maxAmount)) return false;
        return true;
      });
    }
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      txns = txns.filter(txn =>
        (txn.note && txn.note.toLowerCase().includes(searchLower)) ||
        txn.category_name.toLowerCase().includes(searchLower)
      );
    }
    return txns;
  }, [prevSummary, filters, debouncedSearch]);

  // Aggregate daily data for current month based on chartCategory
  const dailyData = useMemo(() => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth() + 1;
    const daysInMonth = new Date(year, monthIndex, 0).getDate();

    let dailyIncome = Array(daysInMonth).fill(0);
    let dailyExpense = Array(daysInMonth).fill(0);

    filteredTransactions.forEach(txn => {
      const day = new Date(txn.txn_date).getDate();
      if (day >= 1 && day <= daysInMonth) {
        if (chartCategory === 'all') {
          if (txn.type === 'income') {
            dailyIncome[day - 1] += parseFloat(txn.amount);
          } else {
            dailyExpense[day - 1] += parseFloat(txn.amount);
          }
        } else if (txn.category_name === chartCategory) {
          if (txn.type === 'income') {
            dailyIncome[day - 1] += parseFloat(txn.amount);
          } else {
            dailyExpense[day - 1] += parseFloat(txn.amount);
          }
        }
      }
    });

    return { dailyIncome, dailyExpense, daysInMonth, labels: Array.from({ length: daysInMonth }, (_, i) => i + 1) };
  }, [filteredTransactions, chartCategory, month]);

  // Aggregate daily data for previous month (for comparison lines)
  const prevDailyData = useMemo(() => {
    if (!prevSummary) return null;
    const prevDate = new Date(month);
    prevDate.setMonth(prevDate.getMonth() - 1);
    const year = prevDate.getFullYear();
    const monthIndex = prevDate.getMonth() + 1;
    const daysInMonth = new Date(year, monthIndex, 0).getDate();

    let dailyIncome = Array(daysInMonth).fill(0);
    let dailyExpense = Array(daysInMonth).fill(0);

    prevFilteredTransactions.forEach(txn => {
      const day = new Date(txn.txn_date).getDate();
      if (day >= 1 && day <= daysInMonth) {
        if (chartCategory === 'all') {
          if (txn.type === 'income') {
            dailyIncome[day - 1] += parseFloat(txn.amount);
          } else {
            dailyExpense[day - 1] += parseFloat(txn.amount);
          }
        } else if (txn.category_name === chartCategory) {
          if (txn.type === 'income') {
            dailyIncome[day - 1] += parseFloat(txn.amount);
          } else {
            dailyExpense[day - 1] += parseFloat(txn.amount);
          }
        }
      }
    });

    return { dailyIncome, dailyExpense, labels: Array.from({ length: daysInMonth }, (_, i) => i + 1) };
  }, [prevFilteredTransactions, chartCategory, month]);

  // Chart data combining current and previous month
  const lineChartData = useMemo(() => {
    if (!dailyData) return null;
    const datasets = [];

    datasets.push({
      label: 'Income',
      data: dailyData.dailyIncome,
      borderColor: '#10b981',
      backgroundColor: 'rgba(16,185,129,0.1)',
      fill: true,
      tension: 0.3,
      pointRadius: 2,
      pointHoverRadius: 5,
      borderWidth: 2,
    });

    datasets.push({
      label: 'Expense',
      data: dailyData.dailyExpense,
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239,68,68,0.1)',
      fill: true,
      tension: 0.3,
      pointRadius: 2,
      pointHoverRadius: 5,
      borderWidth: 2,
    });

    if (prevDailyData) {
      datasets.push({
        label: 'Prev Income',
        data: prevDailyData.dailyIncome,
        borderColor: '#10b981',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 3,
        borderWidth: 1.5,
      });
      datasets.push({
        label: 'Prev Expense',
        data: prevDailyData.dailyExpense,
        borderColor: '#ef4444',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 3,
        borderWidth: 1.5,
      });
    }

    return {
      labels: dailyData.labels,
      datasets,
    };
  }, [dailyData, prevDailyData]);

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        cornerRadius: 12,
        callbacks: {
          label: (ctx) => ` ₹${ctx.raw.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        title: {
          display: true,
          text: 'Day',
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: {
          callback: (value) => `₹${value}`,
        },
      },
    },
  };

  // Compute additional metrics from filtered transactions
  const metrics = useMemo(() => {
    if (!summary) return null;
    const totalIncome = filteredTransactions
      .filter(txn => txn.type === 'income')
      .reduce((sum, txn) => sum + parseFloat(txn.amount), 0);
    const totalExpense = filteredTransactions
      .filter(txn => txn.type === 'expense')
      .reduce((sum, txn) => sum + parseFloat(txn.amount), 0);
    const balance = totalIncome - totalExpense;
    const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpense) / totalIncome) * 100 : 0;

    const daysWithExpense = new Set(
      filteredTransactions
        .filter(txn => txn.type === 'expense')
        .map(txn => new Date(txn.txn_date).getDate())
    ).size || 1;
    const avgDailyExpense = totalExpense / daysWithExpense;

    const expenseByCategory = {};
    filteredTransactions
      .filter(txn => txn.type === 'expense')
      .forEach(txn => {
        expenseByCategory[txn.category_name] = (expenseByCategory[txn.category_name] || 0) + parseFloat(txn.amount);
      });
    let topCategory = null;
    let topCategoryAmount = 0;
    for (const [cat, amount] of Object.entries(expenseByCategory)) {
      if (amount > topCategoryAmount) {
        topCategoryAmount = amount;
        topCategory = cat;
      }
    }

    const transactionCount = filteredTransactions.length;

    return {
      totalIncome,
      totalExpense,
      balance,
      savingsRate,
      avgDailyExpense,
      topCategory,
      transactionCount,
    };
  }, [filteredTransactions, summary]);

  // Category list for filter dropdown
  const allCategoryNames = useMemo(() => {
    if (!summary) return [];
    const catSet = new Set();
    (summary.transactions || []).forEach(txn => catSet.add(txn.category_name));
    return Array.from(catSet).sort();
  }, [summary]);

  const chartCategoryOptions = useMemo(() => {
    return ['all', ...allCategoryNames];
  }, [allCategoryNames]);

  // Month display
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });
  const prevMonthName = new Date(month.getFullYear(), month.getMonth() - 1, 1)
    .toLocaleString('default', { month: 'long', year: 'numeric' });

  // Responsive table rendering: on small screens, hide note column
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              Financial Report
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Monthly overview of your income, expenses, and savings
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Month Navigation */}
            <div className="flex items-center gap-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 px-2 py-1">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors"
                aria-label="Previous month"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-semibold text-slate-900 dark:text-white min-w-[150px] text-center">
                {monthName}
              </span>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors"
                aria-label="Next month"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(prev => !prev)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                showFilters
                  ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {Object.values(filters).some(v => 
                Array.isArray(v) ? v.length > 0 : v !== '' && v !== 'all'
              ) && (
                <span className="ml-1 w-2 h-2 rounded-full bg-white/80 dark:bg-brand-400"></span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 space-y-5 animate-fade-in-up">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Filter Data</h2>
              <button
                onClick={clearFilters}
                className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                Reset All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Type Filter */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                  Transaction Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => updateFilter('type', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="income">Income Only</option>
                  <option value="expense">Expense Only</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                    From
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => updateFilter('startDate', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                    To
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => updateFilter('endDate', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Amount Range */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                    Min ₹
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={filters.minAmount}
                    onChange={(e) => updateFilter('minAmount', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                    Max ₹
                  </label>
                  <input
                    type="number"
                    placeholder="∞"
                    value={filters.maxAmount}
                    onChange={(e) => updateFilter('maxAmount', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Notes or category..."
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Multi-select */}
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">
                Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {allCategoryNames.map(cat => (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      filters.categories.includes(cat)
                        ? 'bg-brand-500 text-white shadow-sm shadow-brand-500/30'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Error Alerts */}
        {(error || prevError) && (
          <div className="space-y-2">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-300">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="flex-1">{error}</span>
                <button
                  onClick={() => fetchSummary(true)}
                  className="px-3 py-1 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-md text-xs font-medium hover:bg-red-200 dark:hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            )}
            {prevError && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-sm text-amber-700 dark:text-amber-300">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="flex-1">Previous month: {prevError}</span>
                <button
                  onClick={() => fetchPrevSummary(true)}
                  className="px-3 py-1 bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-200 rounded-md text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-700"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading Spinner */}
        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent"></div>
          </div>
        ) : !summary ? null : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {/* Balance Card */}
              <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-2xl p-6 text-white shadow-lg shadow-brand-500/20">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium opacity-90">Current Balance</p>
                  <svg className="w-5 h-5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="mt-2">
                  <span className="text-3xl font-bold">₹{metrics.balance.toFixed(2)}</span>
                </div>
                <div className="mt-4 flex items-center text-xs">
                  <span className="px-2 py-1 bg-white/20 rounded-full">
                    vs {prevMonthName}: {metrics.totalIncome > 0 ? 
                      `${((metrics.totalIncome - (prevSummary?.totalIncome || 0)) / (prevSummary?.totalIncome || 1) * 100).toFixed(0)}%` 
                      : 'N/A'}
                  </span>
                </div>
              </div>

              {/* Income Card */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-md border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Income</p>
                  <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-slate-900 dark:text-white">₹{metrics.totalIncome.toFixed(2)}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {metrics.transactionCount} transactions
                </p>
              </div>

              {/* Expense Card */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-md border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Expense</p>
                  <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
                  </svg>
                </div>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-slate-900 dark:text-white">₹{metrics.totalExpense.toFixed(2)}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Avg daily: ₹{metrics.avgDailyExpense.toFixed(2)}
                </p>
              </div>

              {/* Savings Rate Card */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-md border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Savings Rate</p>
                  <svg className="w-5 h-5 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-slate-900 dark:text-white">{metrics.savingsRate.toFixed(1)}%</span>
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Top expense: {metrics.topCategory || 'None'}
                </p>
              </div>
            </div>

            {/* Chart Card */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Daily Income & Expenses
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {chartCategory === 'all' ? 'All categories' : chartCategory}
                  </p>
                </div>
                <select
                  value={chartCategory}
                  onChange={(e) => setChartCategory(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {chartCategoryOptions.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'all' ? 'All Categories' : cat}
                    </option>
                  ))}
                </select>
              </div>
              <div className="h-72">
                {lineChartData && <Line data={lineChartData} options={lineOptions} />}
              </div>
              {prevLoading && (
                <p className="text-xs text-slate-400 mt-2 text-center">Loading comparison data...</p>
              )}
            </div>

            {/* Category Breakdown Table */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="p-6 pb-4">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Category Breakdown</h2>
              </div>
              {(() => {
                const expenseCategories = {};
                const incomeCategories = {};
                filteredTransactions.forEach(txn => {
                  if (txn.type === 'expense') {
                    expenseCategories[txn.category_name] = (expenseCategories[txn.category_name] || 0) + parseFloat(txn.amount);
                  } else {
                    incomeCategories[txn.category_name] = (incomeCategories[txn.category_name] || 0) + parseFloat(txn.amount);
                  }
                });
                const allCategories = [
                  ...Object.entries(expenseCategories).map(([name, amount]) => ({ name, amount, type: 'expense' })),
                  ...Object.entries(incomeCategories).map(([name, amount]) => ({ name, amount, type: 'income' })),
                ].sort((a, b) => b.amount - a.amount);

                return allCategories.length === 0 ? (
                  <p className="text-sm text-slate-400 px-6 pb-6">No categories to display</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-700/50">
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">% of Total</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Distribution</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {allCategories.map((cat, idx) => {
                          const catTotal = cat.type === 'income' ? metrics.totalIncome : metrics.totalExpense;
                          const percent = catTotal > 0 ? (cat.amount / catTotal) * 100 : 0;
                          const color = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
                          return (
                            <tr key={cat.name + cat.type} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                              <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-white">{cat.name}</td>
                              <td className="px-6 py-4">
                                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                                  cat.type === 'income'
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                }`}>
                                  {cat.type}
                                </span>
                              </td>
                              <td className={`px-6 py-4 text-right text-sm font-semibold ${
                                cat.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                              }`}>
                                ₹{cat.amount.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-slate-500 dark:text-slate-400">
                                {percent.toFixed(0)}%
                              </td>
                              <td className="px-6 py-4 w-48">
                                <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(percent, 100)}%`, backgroundColor: color }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            {/* Transactions Table */}
            {filteredTransactions.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="p-6 pb-4">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Transactions <span className="text-sm font-normal text-slate-500 dark:text-slate-400">({filteredTransactions.length})</span>
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-700/50">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Amount</th>
                        {!isMobile && (
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Note</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {filteredTransactions.map(txn => (
                        <tr key={txn.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                          <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300 whitespace-nowrap">{txn.txn_date}</td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-white">{txn.category_name}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                              txn.type === 'income'
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {txn.type}
                            </span>
                          </td>
                          <td className={`px-6 py-4 text-right text-sm font-semibold whitespace-nowrap ${
                            txn.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {txn.type === 'income' ? '+' : '-'} ₹{parseFloat(txn.amount).toFixed(2)}
                          </td>
                          {!isMobile && (
                            <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">{txn.note || '—'}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}